import { useState, useMemo, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import { XCircleIcon } from '@heroicons/react/16/solid'

import MiniSearch from 'minisearch'

import MHintsList from '@/components/molecules/project-details/MHintsList'
import OHintDetails from '@/components/organisms/project-details/OHintDetailsView'
import OHintEditorDialog from '@/components/organisms/project-details/OHintEditorDialog'
import { Button } from '@/components/catalyst/button'

import { Listbox, ListboxLabel, ListboxOption } from '@/components/catalyst/listbox'
import { Input } from '@/components/catalyst/input'

import { Checkbox, CheckboxField, CheckboxGroup } from '@/components/catalyst/checkbox'
import { Field, Description, Fieldset, Label } from '@/components/catalyst/fieldset'

import { getHintsForProject } from '@/services/Firebase'

import { getTemplatedHint, getAvailableHintRoleTypes } from '@/lib/hints-helpers'

const ROLE_SHOW_ALL = 'All roles'
const ALL_HINT_ROLE_TYPES = [ROLE_SHOW_ALL, ...getAvailableHintRoleTypes()]

const SERVICES_SHOW_ALL = 'All Services'

const LIBRARIES_SHOW_ALL = 'All Libraries'

export default function OProjectHintListAndDetails({
  organizationId = null,
  projectId = null,
  lastTick,
}) {
  const [lastUpdatedAt, setLastUpdatedAt] = useState(Date.now().toString())
  const [searchParams, setSearchParams] = useSearchParams()
  const [isHintCreating] = useState(false)
  const [hints, setHints] = useState([])
  const [showOnlyHumanHints, setShowOnlyHumanHints] = useState(false)
  const [hintsLoading, setHintsLoading] = useState(true)
  const [reloadTrigger, setReloadTrigger] = useState(true)
  const [showHintEditor, setShowHintEditor] = useState(false)
  const [searchPhrase, setSearchPhrase] = useState('')

  const [selectedRoleToFilter, setSelectedRoleToFilter] = useState(ROLE_SHOW_ALL)
  const [selectedServiceToFilter, setSelectedServiceToFilter] = useState(SERVICES_SHOW_ALL)
  const [selectedLibraryToFilter, setSelectedLibraryToFilter] = useState(LIBRARIES_SHOW_ALL)

  const searchProvider = useMemo(() => {
    const options = {
      fields: ['title', 'context', 'value'],
      storeFields: ['id'],
    }
    const miniSearch = new MiniSearch(options)
    miniSearch.addAll(hints)
    return miniSearch
  }, [hints])

  const { allServices, allLibraries } = useMemo(() => {
    const allServices = new Set()
    const allLibraries = new Set()

    hints.forEach(hint => {
      hint.services.forEach(service => allServices.add(service))
      hint.libraries.forEach(library => allLibraries.add(library))
    })

    return {
      allServices: [...allServices],
      allLibraries: [...allLibraries],
    }
  }, [hints])

  const allServicesForFilter = useMemo(() => {
    return [SERVICES_SHOW_ALL, ...allServices]
  }, [allServices])

  const allLibrariesForFilter = useMemo(() => {
    return [LIBRARIES_SHOW_ALL, ...allLibraries]
  }, [allLibraries])

  const newHintTemplate = useMemo(() => {
    return getTemplatedHint({
      organization_id: organizationId,
      project_id: projectId,
      libraries: allLibraries,
      services: allServices,
    })
  }, [organizationId, projectId, allLibraries, allServices])

  useEffect(() => {
    if (projectId && reloadTrigger) {
      setReloadTrigger(false)
      getHintsForProject({ projectId })
        .then(hints => {
          setHints(hints.data)
          setHintsLoading(false)
          // console.table(hints.data)
        })
        .catch(error => {
          console.log(error)
          setHintsLoading(false)
        })
    }
  }, [projectId, reloadTrigger])

  const handleActiveHintSelected = useCallback(
    selectedHintId => {
      if (selectedHintId) {
        setSearchParams({ hint: selectedHintId })
        setActiveHintId(selectedHintId)
      } else {
        setSearchParams({})
        setActiveHintId(null)
      }
    },
    [setSearchParams]
  )

  useEffect(() => {
    setLastUpdatedAt(Date.now().toString())
  }, [newHintTemplate])

  const handleHintUpdated = useCallback(
    hint => {
      const hintData = hint.data || {}

      setHints(state => {
        // Check if the hint already exists using 'findIndex'
        const existingIndex = state.findIndex(h => h.id === hintData.id)

        if (existingIndex !== -1) {
          // Hint exists - update
          return [
            ...state.slice(0, existingIndex), // Elements before the updated hint
            hintData, // The updated hint
            ...state.slice(existingIndex + 1), // Elements after the updated hint
          ]
        } else {
          // Hint is new - add to the end
          return [...state, hintData]
        }
      })
      handleActiveHintSelected(hintData?.id)
      setLastUpdatedAt(Date.now().toString())
    },
    [handleActiveHintSelected]
  )

  const handleHintDeleted = useCallback(
    hintId => {
      setHints(state => state.filter(hint => hint.id !== hintId))
      handleActiveHintSelected(null)
    },
    [handleActiveHintSelected]
  )

  const hintId = useMemo(() => {
    return searchParams.get('hint')
  }, [searchParams])

  const [activeHintId, setActiveHintId] = useState(hintId)

  // Set active iteration id from URL
  useEffect(() => {
    setActiveHintId(hintId)
  }, [hintId])

  const activeHint = useMemo(() => {
    if (!activeHintId || !hints) {
      return null
    }
    return hints?.find(hint => hint.id === activeHintId) || null
  }, [activeHintId, hints])

  const filteredHints = useMemo(() => {
    let results = hints
    if (searchPhrase && searchPhrase !== '' && searchPhrase.length > 2) {
      const searchSesults = searchProvider.search(searchPhrase, {
        boost: { title: 2 },
        prefix: true,
      })
      results = searchSesults.map(result => hints.find(hint => hint.id === result.id))
    }

    if (showOnlyHumanHints) {
      results = results.filter(hint => hint.auto_generated === false)
    }

    if (selectedRoleToFilter !== ROLE_SHOW_ALL) {
      results = results.filter(hint => hint.role_types.includes(selectedRoleToFilter))
    }

    if (selectedServiceToFilter !== SERVICES_SHOW_ALL) {
      results = results.filter(hint => hint.services.includes(selectedServiceToFilter))
    }

    if (selectedLibraryToFilter !== LIBRARIES_SHOW_ALL) {
      results = results.filter(hint => hint.libraries.includes(selectedLibraryToFilter))
    }

    return results
  }, [
    selectedRoleToFilter,
    searchPhrase,
    hints,
    searchProvider,
    showOnlyHumanHints,
    selectedServiceToFilter,
    selectedLibraryToFilter,
  ])

  const handleRoleTypeFilterChange = useCallback(selectedRoleType => {
    setSelectedRoleToFilter(selectedRoleType)
  }, [])

  const handleServiceFilterChange = useCallback(selectedService => {
    setSelectedServiceToFilter(selectedService)
  }, [])

  const handleLibraryFilterChange = useCallback(selectedLibrary => {
    setSelectedLibraryToFilter(selectedLibrary)
  }, [])

  const handleSearchPhraseChange = useCallback(phrase => {
    setSearchPhrase(phrase)
  }, [])

  const handleClearFilters = useCallback(() => {
    setSelectedRoleToFilter(ROLE_SHOW_ALL)
    setSelectedServiceToFilter(SERVICES_SHOW_ALL)
    setSelectedLibraryToFilter(LIBRARIES_SHOW_ALL)
    setSearchPhrase('')
  }, [])

  return (
    <div className="mx-auto flex w-full items-start pt-4">
      <aside className="mb-24 block w-full shrink-0 overflow-y-scroll rounded-xl bg-white shadow-md">
        <h3 className="flex items-center justify-between border-b  border-neutral-200 bg-white p-4 text-xl font-semibold text-zinc-700">
          Available hints{' '}
          <div className="flex-shrink-0 flex-grow-0">
            <Button className="" onClick={() => setShowHintEditor(true)}>
              Add hint
            </Button>
          </div>
        </h3>
        <div className="flex">
          <div className="m-2 w-1/3 rounded-md bg-zinc-100 p-2 shadow-inner">
            <div className="flex justify-between p-2">
              <Input
                placeholder="Search"
                value={searchPhrase}
                onChange={e => {
                  handleSearchPhraseChange(e.target.value)
                }}
              />{' '}
              <Button
                plain
                color="neutral"
                className="ml-1 flex-shrink-0"
                onClick={() => {
                  setSearchPhrase('')
                }}
              >
                <XCircleIcon />
              </Button>
            </div>
            <div className="p-2">
              <Listbox
                name="role-types-filter"
                onChange={e => handleRoleTypeFilterChange(e)}
                value={selectedRoleToFilter}
              >
                {ALL_HINT_ROLE_TYPES?.map(role => (
                  <ListboxOption key={role} value={role}>
                    <ListboxLabel className="capitalize">{role}</ListboxLabel>
                  </ListboxOption>
                ))}
              </Listbox>
            </div>
            <div className=" p-4">
              <Fieldset>
                <CheckboxGroup>
                  <CheckboxField>
                    <Checkbox
                      name="showOnlyHumanHints"
                      checked={showOnlyHumanHints}
                      onChange={e => {
                        setShowOnlyHumanHints(e)
                      }}
                    />
                    <Label>Show only human generated</Label>
                    <Description>Filter our auto-generated hints.</Description>
                  </CheckboxField>
                </CheckboxGroup>
              </Fieldset>
            </div>
            <div className="p-2">
              <Button
                outline
                color="neutral"
                className="ml-1 flex-shrink-0"
                onClick={handleClearFilters}
              >
                <XCircleIcon /> Clear all filters
              </Button>
            </div>
          </div>
          <div className="m-2 w-1/3 rounded-md bg-zinc-100 p-2 shadow-inner">
            <div className="p-2">
              <Field>
                <Label>Filter by Service</Label>
                <Listbox
                  name="services-filter"
                  onChange={e => handleServiceFilterChange(e)}
                  value={selectedServiceToFilter}
                >
                  {allServicesForFilter?.map(service => (
                    <ListboxOption key={service} value={service}>
                      <ListboxLabel className="">{service}</ListboxLabel>
                    </ListboxOption>
                  ))}
                </Listbox>
              </Field>
            </div>
            <div className="p-2 pt-4">
              <Field>
                <Label>Filter by Library</Label>
                <Listbox
                  name="libraries-filter"
                  onChange={e => handleLibraryFilterChange(e)}
                  value={selectedLibraryToFilter}
                >
                  {allLibrariesForFilter?.map(service => (
                    <ListboxOption key={service} value={service}>
                      <ListboxLabel className="">{service}</ListboxLabel>
                    </ListboxOption>
                  ))}
                </Listbox>
              </Field>
            </div>
          </div>
        </div>
        <MHintsList
          hints={filteredHints}
          isLoading={hintsLoading}
          activeHintId={activeHintId}
          onActiveSelected={handleActiveHintSelected}
          lastTick={lastTick}
          isHintCreating={isHintCreating}
        />
      </aside>

      {!!activeHint && (
        <OHintDetails
          key={`${activeHintId}-${lastUpdatedAt}`}
          hint={activeHint}
          scopeOrganizationId={organizationId}
          scopeProjectId={projectId}
          onHintUpdated={handleHintUpdated}
          onHintDeleted={handleHintDeleted}
          onCanceled={() => handleActiveHintSelected(null)}
        />
      )}

      <OHintEditorDialog
        key={lastUpdatedAt}
        isOpen={showHintEditor}
        onClose={() => setShowHintEditor(false)}
        hint={newHintTemplate}
        scopeOrganizationId={organizationId}
        scopeProjectId={projectId}
        onSaveFinished={handleHintUpdated}
      />
    </div>
  )
}

OProjectHintListAndDetails.propTypes = {
  lastTick: PropTypes.number,
  organizationId: PropTypes.string,
  projectId: PropTypes.string,
}
